import { useContent } from "@/lib/content-context";
import { fileUrl } from "@/lib/api";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { Coffee, Home, Users, Sparkles, Heart, BookOpen, Leaf, Sun, Award, Compass } from "lucide-react";

const ICON_MAP = { Coffee, Home, Users, Sparkles, Heart, BookOpen, Leaf, Sun, Award, Compass };

const PHILOSOPHY_FALLBACK = {
    label: "What We Believe",
    heading: "Every cup has a mood. Every visit has a feeling.",
    watermark_eyebrow: "Your Happy Place",
    watermark: "HIDEOUT",
    cards: [],
};
const FOUNDER_FALLBACK = { label: "The Mind Behind It", name: "", role: "", quote: "", paragraphs: [], signoff: "", image: "" };
const TEAM_FALLBACK = { label: "The People", heading: "", members: [] };
const VIDEO_FALLBACK = { label: "Watch The Hideout", heading: "", subtitle: "", youtube_id: "" };

export default function About() {
    const { content } = useContent();
    if (!content) return null;
    const a = content.about || {};
    const philosophy = { ...PHILOSOPHY_FALLBACK, ...(a.philosophy || {}) };
    const founder = { ...FOUNDER_FALLBACK, ...(a.founder || {}) };
    const team = { ...TEAM_FALLBACK, ...(a.team || {}) };
    const video = { ...VIDEO_FALLBACK, ...(a.video_section || {}) };

    return (
        <div className="bg-cream-light pt-28" data-testid="about-page">
            <SEO title="About" description={a.intro} image={fileUrl(a.hero_image)} path="/about" />

            {/* SECTION 1 — HERO (tighter spacing, balanced image) */}
            <section className="relative px-6 md:px-12 pt-4 pb-16 md:pt-2 md:pb-20" data-testid="about-hero">
                <div className="max-w-[1480px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <Reveal className="lg:col-span-7">
                        <div className="tracked text-coffee/50 text-[10px] mb-5">About</div>
                        <h1
                            className="font-editorial font-bold text-coffee leading-[0.95] mb-6"
                            style={{ fontSize: "clamp(2.4rem, 5.6vw, 5rem)" }}
                            data-testid="about-title"
                        >
                            {a.title}
                        </h1>
                        <p className="font-body text-coffee/80 max-w-xl text-base md:text-lg leading-[1.75]" data-testid="about-intro">
                            {a.intro}
                        </p>
                    </Reveal>
                    <Reveal delay={0.12} className="lg:col-span-5">
                        <div className="aspect-[4/5] lg:aspect-[5/6] max-h-[520px] overflow-hidden bg-coffee/10 mx-auto">
                            <img
                                src={fileUrl(a.hero_image)}
                                alt="About Cafe Hideout"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 2 — Existing "Roots / Beans / Hideout" (unchanged copy) */}
            <section className="px-6 md:px-12 py-16 md:py-24" data-testid="about-sections">
                <div className="max-w-[1100px] mx-auto space-y-20">
                    {a.sections?.map((s, i) => (
                        <Reveal key={i} delay={i * 0.05}>
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 border-t border-coffee/15 pt-12">
                                <div className="md:col-span-4">
                                    <div className="font-mono tracked text-coffee/40 text-[10px] mb-3">0{i + 1}</div>
                                    <h3 className="font-editorial font-bold text-coffee text-4xl">{s.heading}</h3>
                                </div>
                                <div className="md:col-span-8">
                                    <p className="font-body text-coffee/80 text-lg leading-[1.8]">{s.body}</p>
                                </div>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* SECTION 3 — BRAND PHILOSOPHY (dark) */}
            <section
                className="relative overflow-hidden px-6 md:px-12 py-24 md:py-32"
                style={{ backgroundColor: "#1a0f00" }}
                data-testid="about-philosophy"
            >
                <div className="grain-light pointer-events-none" />
                <div className="relative max-w-[1280px] mx-auto">
                    <Reveal className="text-center">
                        <div className="tracked text-gold text-[10px] mb-6">{philosophy.label}</div>
                        <h2
                            className="font-editorial font-bold text-cream leading-[1.05] max-w-3xl mx-auto"
                            style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}
                        >
                            {philosophy.heading}
                        </h2>
                    </Reveal>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-16 md:mt-20">
                        {philosophy.cards.map((c, i) => {
                            const Icon = ICON_MAP[c.icon] || Sparkles;
                            return (
                                <Reveal key={i} delay={i * 0.08}>
                                    <div
                                        className="group h-full bg-coffee-dark/40 border border-cream/10 hover:border-gold transition-all duration-500 p-8 md:p-10"
                                        data-testid={`philosophy-card-${i}`}
                                    >
                                        <Icon size={28} strokeWidth={1.2} className="text-gold mb-7 transition-transform duration-500 group-hover:-translate-y-1" />
                                        <h3 className="font-editorial font-bold text-gold text-2xl md:text-3xl mb-4">{c.title}</h3>
                                        <p className="font-body font-light text-cream/70 text-[15px] leading-[1.75]">{c.body}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>

                    {/* Bottom watermark statement */}
                    <Reveal delay={0.2} className="relative mt-24 md:mt-32 text-center">
                        <div className="relative inline-block">
                            <div
                                aria-hidden
                                className="font-editorial font-bold leading-none select-none pointer-events-none"
                                style={{ fontSize: "clamp(3.5rem, 8vw, 7rem)", color: "#d4a574", opacity: 0.18 }}
                            >
                                {philosophy.watermark}
                            </div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="tracked text-gold text-[10px]">{philosophy.watermark_eyebrow}</span>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 4 — FOUNDER (asymmetric) */}
            <section
                className="relative px-6 md:px-12 py-24 md:py-32"
                style={{ backgroundColor: "#faf6ef" }}
                data-testid="about-founder"
            >
                <div className="max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    {/* Left — content */}
                    <Reveal className="lg:col-span-7 order-2 lg:order-1">
                        <div className="tracked text-coffee/50 text-[10px] mb-6">{founder.label}</div>
                        <h2 className="font-editorial font-bold text-coffee leading-[1.02] mb-3" style={{ fontSize: "clamp(2.2rem, 4.5vw, 3rem)" }}>
                            {founder.name}
                        </h2>
                        <div className="font-body text-coffee/60 text-sm mb-10">{founder.role}</div>

                        {founder.quote && (
                            <blockquote className="relative pl-6 border-l-2 border-gold mb-10">
                                <p className="font-editorial italic text-2xl md:text-3xl leading-[1.35]" style={{ color: "#8a6b3d" }}>
                                    "{founder.quote}"
                                </p>
                            </blockquote>
                        )}

                        <div className="space-y-6 font-body text-coffee/85 text-base md:text-[17px] leading-[1.85] max-w-[58ch]">
                            {(founder.paragraphs || []).map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>

                        {founder.signoff && (
                            <div className="mt-12 font-editorial italic text-2xl md:text-3xl" style={{ color: "#b88d52" }}>
                                {founder.signoff}
                            </div>
                        )}
                    </Reveal>

                    {/* Right — portrait */}
                    <Reveal delay={0.1} className="lg:col-span-5 order-1 lg:order-2">
                        <div className="relative">
                            <div className="aspect-[4/5] overflow-hidden bg-coffee/10">
                                <img src={fileUrl(founder.image)} alt={founder.name} className="w-full h-full object-cover" />
                            </div>
                            <div className="absolute -bottom-3 -right-3 lg:-bottom-5 lg:-right-5 px-4 py-2 bg-coffee text-gold tracked text-[10px]">
                                Founder · {founder.name?.split(" ")[0]}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SECTION 5 — TEAM */}
            <section className="relative px-6 md:px-12 py-24 md:py-32 bg-white" data-testid="about-team">
                <div className="max-w-[1480px] mx-auto">
                    <Reveal className="text-center">
                        <div className="tracked text-coffee/50 text-[10px] mb-6">{team.label}</div>
                        <h2 className="font-editorial font-bold text-coffee leading-[1.05] max-w-3xl mx-auto"
                            style={{ fontSize: "clamp(1.9rem, 3.5vw, 2.5rem)" }}>
                            {team.heading}
                        </h2>
                    </Reveal>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12 mt-16 md:mt-20">
                        {team.members.map((m, i) => (
                            <Reveal key={i} delay={i * 0.06}>
                                <div className="group" data-testid={`team-card-${i}`}>
                                    <div className="aspect-square overflow-hidden bg-coffee/5 mb-6">
                                        <img
                                            src={fileUrl(m.image)}
                                            alt={m.name}
                                            loading="lazy"
                                            className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.04]"
                                        />
                                    </div>
                                    <h3 className="font-editorial font-bold text-coffee text-xl md:text-2xl inline-block relative">
                                        {m.name}
                                        <span className="block absolute left-0 -bottom-1 h-[2px] bg-gold transition-all duration-500 w-0 group-hover:w-full" />
                                    </h3>
                                    <div className="font-body font-light text-[13px] tracking-[0.18em] uppercase mt-2" style={{ color: "#b88d52" }}>
                                        {m.role}
                                    </div>
                                    {m.quote && (
                                        <p className="font-editorial italic text-coffee/65 text-[15px] leading-[1.55] mt-4">
                                            "{m.quote}"
                                        </p>
                                    )}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* SECTION 6 — VIDEO CLOSE */}
            {video.youtube_id && (
                <section
                    className="relative px-6 md:px-12 py-24 md:py-32"
                    style={{ backgroundColor: "#1a0f00" }}
                    data-testid="about-video"
                >
                    <div className="grain-light pointer-events-none" />
                    <div className="relative max-w-[1280px] mx-auto">
                        <Reveal className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
                            <div className="tracked text-gold text-[10px] mb-6">{video.label}</div>
                            <h2 className="font-editorial font-bold text-cream leading-[1.05]"
                                style={{ fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                                {video.heading}
                            </h2>
                            {video.subtitle && (
                                <p className="font-body text-cream/65 mt-5 text-base md:text-lg leading-relaxed">
                                    {video.subtitle}
                                </p>
                            )}
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="relative aspect-video overflow-hidden bg-coffee shadow-[0_30px_80px_-30px_rgba(0,0,0,0.7)] mx-auto" style={{ maxWidth: "70%" }}>
                                <iframe
                                    className="absolute inset-0 w-full h-full"
                                    src={`https://www.youtube.com/embed/${video.youtube_id}?rel=0&modestbranding=1`}
                                    title="Cafe Hideout brand film"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    data-testid="about-youtube-iframe"
                                />
                            </div>
                        </Reveal>
                    </div>
                </section>
            )}
        </div>
    );
}
